import {
  useId,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  type InputHTMLAttributes,
} from "react";
import { useLanguage } from "../i18n/LanguageProvider";

interface FieldShellProps {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string | undefined;
  children: (ids: {
    inputId: string;
    describedBy: string | undefined;
    invalid: boolean;
  }) => ReactNode;
  counter?: string;
}

/**
 * Label, hint, control and error as one accessible unit.
 *
 * The label is a real `<label for>`, the hint and error are wired through
 * `aria-describedby`, and the error carries `role="alert"` so it is
 * announced when it appears. Centralising this is what keeps every form on
 * the site accessible rather than only the ones someone remembered to wire
 * up.
 */
export const Field = ({
  label,
  required,
  hint,
  error,
  children,
  counter,
}: FieldShellProps) => {
  const { t } = useLanguage();
  const inputId = useId();
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="gw-field">
      <label className="gw-field__label" htmlFor={inputId}>
        {label}
        {required ? (
          <span className="gw-field__required" aria-hidden="true">
            *
          </span>
        ) : null}
        {required ? (
          <span className="gw-visually-hidden"> ({t("form.required")})</span>
        ) : null}
      </label>

      {hint ? (
        <span className="gw-field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}

      {children({
        inputId,
        describedBy: describedBy || undefined,
        invalid: Boolean(error),
      })}

      {counter ? <span className="gw-field__counter">{counter}</span> : null}

      {error ? (
        <span className="gw-field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
};

type TextFieldProps = {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string | undefined;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "className">;

export const TextField = ({
  label,
  required,
  hint,
  error,
  ...inputProps
}: TextFieldProps) => (
  <Field
    label={label}
    {...(required !== undefined ? { required } : {})}
    {...(hint !== undefined ? { hint } : {})}
    error={error}
  >
    {({ inputId, describedBy, invalid }) => (
      <input
        {...inputProps}
        id={inputId}
        className="gw-input"
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        required={required}
      />
    )}
  </Field>
);

type TextAreaFieldProps = {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string | undefined;
  maxLength?: number;
  showCounter?: boolean;
} & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id" | "className">;

export const TextAreaField = ({
  label,
  required,
  hint,
  error,
  maxLength,
  showCounter = true,
  ...textareaProps
}: TextAreaFieldProps) => {
  const { t } = useLanguage();
  const currentLength = String(textareaProps.value ?? "").length;
  const counter =
    showCounter && maxLength
      ? t("form.charactersRemaining", {
          count: Math.max(0, maxLength - currentLength),
        })
      : undefined;

  return (
    <Field
      label={label}
      {...(required !== undefined ? { required } : {})}
      {...(hint !== undefined ? { hint } : {})}
      error={error}
      {...(counter !== undefined ? { counter } : {})}
    >
      {({ inputId, describedBy, invalid }) => (
        <textarea
          {...textareaProps}
          id={inputId}
          className="gw-textarea"
          maxLength={maxLength}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          required={required}
        />
      )}
    </Field>
  );
};

type SelectFieldProps = {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string | undefined;
  options: ReadonlyArray<{ value: string; label: string }>;
  placeholder?: string;
} & Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "id" | "className" | "children"
>;

export const SelectField = ({
  label,
  required,
  hint,
  error,
  options,
  placeholder,
  ...selectProps
}: SelectFieldProps) => (
  <Field
    label={label}
    {...(required !== undefined ? { required } : {})}
    {...(hint !== undefined ? { hint } : {})}
    error={error}
  >
    {({ inputId, describedBy, invalid }) => (
      <select
        {...selectProps}
        id={inputId}
        className="gw-select"
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        required={required}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    )}
  </Field>
);

interface ConsentCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  error?: string | undefined;
  name: string;
}

/**
 * A consent control.
 *
 * Never defaults to checked: `checked` is driven entirely by state that
 * starts false, and there is no `defaultChecked` anywhere. Agreement has to
 * be an action the person took.
 */
export const ConsentCheckbox = ({
  checked,
  onChange,
  children,
  error,
  name,
}: ConsentCheckboxProps) => {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <div className="gw-field">
      <label className="gw-checkbox" htmlFor={id}>
        <input
          id={id}
          name={name}
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
        <span className="gw-checkbox__text">{children}</span>
      </label>
      {error ? (
        <span className="gw-field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
};

/**
 * Hidden decoy input.
 *
 * Legitimate users never see or focus it, so a non-empty value is a strong
 * bot signal. `tabIndex={-1}` and `aria-hidden` keep it out of the keyboard
 * order and the accessibility tree, and `autoComplete="off"` stops a
 * password manager filling it in.
 */
export const HoneypotField = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => (
  <div className="gw-honeypot" aria-hidden="true">
    <label htmlFor="gw-website-field">Leave this field empty</label>
    <input
      id="gw-website-field"
      name="website"
      type="text"
      tabIndex={-1}
      autoComplete="off"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  </div>
);

interface SubmitButtonProps {
  pending: boolean;
  label: string;
  pendingLabel: string;
  disabled?: boolean;
  variant?: "primary" | "secondary";
}

/**
 * Submit control that cannot be double-fired.
 *
 * Disabling while pending is the first line of defence against duplicate
 * records; the client request id sent with the body is the one that
 * actually guarantees it, since a disabled button does not stop a retried
 * network request.
 */
export const SubmitButton = ({
  pending,
  label,
  pendingLabel,
  disabled = false,
  variant = "primary",
}: SubmitButtonProps) => (
  <button
    type="submit"
    className={`gw-btn gw-btn--${variant}`}
    disabled={pending || disabled}
    aria-busy={pending}
  >
    {pending ? <span className="gw-spinner" aria-hidden="true" /> : null}
    {pending ? pendingLabel : label}
  </button>
);
